/**
 * AdminPromos — Promo-code editor for admins.
 *
 * Lists every row in the `promo_codes` collection with usage stats, and lets
 * an admin flip `max_uses`, `applies_to`, `disabled`, `unlimited_per_user`,
 * `expires_at`, and `percent` inline without needing mongosh access.
 *
 * Backend endpoints used:
 *   GET   /api/admin/promo             → list all
 *   PATCH /api/admin/promo/{code}      → partial update
 *   POST  /api/admin/promo             → create/upsert
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Save, Plus, Search, Ban, CheckCircle2, RefreshCw, Infinity as InfIcon } from "lucide-react";

const APPLIES_TO_OPTIONS = [
  { value: "any", label: "Any package" },
  { value: "single_report", label: "Single report ($99)" },
  { value: "credit_bundle_3", label: "3-report bundle ($249)" },
  { value: "credit_bundle_5", label: "5-report bundle ($375)" },
  { value: "credit_bundle_10", label: "10-report bundle ($650)" },
  { value: "credit_bundle_20", label: "20-report bundle ($1,100)" },
];

const AdminPromos = () => {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingCode, setSavingCode] = useState(null);
  const [drafts, setDrafts] = useState({}); // code -> { max_uses, applies_to, disabled, ... }
  const [showCreate, setShowCreate] = useState(false);
  const [newPromo, setNewPromo] = useState({ code: "", percent: 100, applies_to: "single_report",
                                              max_uses: 1, unlimited_per_user: false });

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/promo");
      setItems(r.data || []);
      // reset drafts so unsaved local edits don't linger after reload
      setDrafts({});
    } catch (e) {
      if (e?.response?.status === 403) { toast.error("Admin access required"); nav("/"); }
      else toast.error("Failed to load promo codes");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  const filtered = useMemo(() => {
    const term = q.trim().toUpperCase();
    if (!term) return items;
    return items.filter((it) => (it.code || "").toUpperCase().includes(term));
  }, [items, q]);

  const draftFor = (row) => ({ ...row, ...(drafts[row.code] || {}) });
  const patch = (code, key, value) =>
    setDrafts((d) => ({ ...d, [code]: { ...(d[code] || {}), [key]: value } }));
  const isDirty = (code) => Object.keys(drafts[code] || {}).length > 0;

  const save = async (row) => {
    const changes = drafts[row.code];
    if (!changes || Object.keys(changes).length === 0) return;
    setSavingCode(row.code);
    try {
      const r = await api.patch(`/admin/promo/${encodeURIComponent(row.code)}`, changes);
      // Replace the row in-place and drop the draft.
      setItems((its) => its.map((it) => (it.code === row.code ? { ...it, ...r.data } : it)));
      setDrafts((d) => { const c = { ...d }; delete c[row.code]; return c; });
      toast.success(`${row.code} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingCode(null); }
  };

  const create = async () => {
    const code = (newPromo.code || "").trim().toUpperCase();
    if (!code) { toast.error("Code required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/promo", { ...newPromo, code });
      toast.success(`${code} created`);
      setShowCreate(false);
      setNewPromo({ code: "", percent: 100, applies_to: "single_report", max_uses: 1, unlimited_per_user: false });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--paper)" }}>
      <Header />
      <main className="flex-1 py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div>
              <div className="overline">Admin · Promo codes</div>
              <h1 className="font-display text-3xl mt-1" style={{ color: "var(--char)" }}>Promo code editor</h1>
              <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
                Edit <span className="font-mono">max_uses</span>, <span className="font-mono">applies_to</span>,
                and <span className="font-mono">disabled</span> without needing mongosh.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={load} disabled={busy}
                        className="btn-secondary !py-2 !px-3 text-sm" data-testid="admin-promo-refresh">
                <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Refresh
              </button>
              <button onClick={() => setShowCreate((s) => !s)}
                        className="btn-primary !py-2 !px-3 text-sm" data-testid="admin-promo-new">
                <Plus size={14} /> New code
              </button>
            </div>
          </div>

          {showCreate && (
            <div className="card p-4 mb-6" data-testid="admin-promo-create-form">
              <div className="text-sm font-semibold mb-3" style={{ color: "var(--char)" }}>Create a new promo code</div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <Field label="Code" value={newPromo.code}
                          onChange={(v) => setNewPromo({ ...newPromo, code: v.toUpperCase() })}
                          mono testid="admin-promo-new-code" />
                <Field label="Percent" type="number" value={newPromo.percent}
                          onChange={(v) => setNewPromo({ ...newPromo, percent: Number(v) })} testid="admin-promo-new-percent" />
                <Field label="Max uses" type="number" value={newPromo.max_uses}
                          onChange={(v) => setNewPromo({ ...newPromo, max_uses: Number(v) })} testid="admin-promo-new-max" />
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider block mb-1" style={{ color: "var(--muted)" }}>Applies to</label>
                  <select value={newPromo.applies_to}
                             onChange={(e) => setNewPromo({ ...newPromo, applies_to: e.target.value })}
                             className="w-full px-3 py-2 text-sm rounded"
                             style={{ border: "1px solid var(--hairline)" }}
                             data-testid="admin-promo-new-applies">
                    {APPLIES_TO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-2 text-xs mt-6" style={{ color: "var(--char)" }}>
                  <input type="checkbox" checked={newPromo.unlimited_per_user}
                            onChange={(e) => setNewPromo({ ...newPromo, unlimited_per_user: e.target.checked })}
                            data-testid="admin-promo-new-unlimited" />
                  <span>Unlimited per user</span>
                </label>
              </div>
              <div className="flex gap-2 mt-3 justify-end">
                <button onClick={() => setShowCreate(false)} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
                <button onClick={create} disabled={busy} className="btn-primary !py-2 !px-4 text-sm" data-testid="admin-promo-new-save">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Create
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--muted)" }} />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by code (e.g. FIRSTPASS)"
                className="w-full pl-9 pr-3 py-2 text-sm rounded"
                style={{ border: "1px solid var(--hairline)", background: "var(--white)" }}
                data-testid="admin-promo-search"
              />
            </div>
            <div className="text-xs" style={{ color: "var(--muted)" }}>
              {filtered.length} / {items.length} codes
            </div>
          </div>

          {busy && !items.length ? (
            <div className="text-center py-16" style={{ color: "var(--muted)" }}>
              <Loader2 size={22} className="animate-spin inline" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="card p-10 text-center" style={{ color: "var(--muted)" }} data-testid="admin-promo-empty">
              {q ? "No codes match your search." : "No promo codes yet."}
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-promo-list">
              {filtered.map((row) => {
                const d = draftFor(row);
                const used = int(d.used_count);
                const cap = int(d.max_uses);
                const remaining = Math.max(0, cap - used);
                return (
                  <div key={row.code} className="card p-4" data-testid={`admin-promo-row-${row.code}`}
                          style={{ border: d.disabled ? "1px dashed #C0392B" : undefined }}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-mono text-lg font-bold" style={{ color: "var(--char)" }}>{row.code}</div>
                          {d.disabled && (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded"
                                     style={{ background: "#FDECEC", color: "#7A1F17" }}>
                              <Ban size={10} className="inline" /> Disabled
                            </span>
                          )}
                          {d.unlimited_per_user && (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded"
                                     style={{ background: "#E4F3E7", color: "#1A6F2B" }}>
                              <InfIcon size={10} className="inline" /> Unlimited per user
                            </span>
                          )}
                        </div>
                        <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                          {d.percent}% off · used {used} / {cap} ({remaining} remaining)
                          {row.last_redeemed_at && <> · last redeemed {row.last_redeemed_at.split("T")[0]}</>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => save(row)}
                          disabled={!isDirty(row.code) || savingCode === row.code}
                          className="btn-primary !py-1.5 !px-3 text-xs"
                          data-testid={`admin-promo-save-${row.code}`}
                        >
                          {savingCode === row.code ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                          Save
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                      <Field label="Max uses" type="number" value={d.max_uses}
                                onChange={(v) => patch(row.code, "max_uses", Number(v))}
                                testid={`admin-promo-maxuses-${row.code}`} />
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wider block mb-1" style={{ color: "var(--muted)" }}>Applies to</label>
                        <select
                          value={d.applies_to || "single_report"}
                          onChange={(e) => patch(row.code, "applies_to", e.target.value)}
                          className="w-full px-3 py-2 text-sm rounded"
                          style={{ border: "1px solid var(--hairline)" }}
                          data-testid={`admin-promo-applies-${row.code}`}
                        >
                          {APPLIES_TO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <Field label="Percent" type="number" value={d.percent}
                                onChange={(v) => patch(row.code, "percent", Number(v))}
                                testid={`admin-promo-percent-${row.code}`} />
                      <div className="flex flex-col gap-2 justify-end">
                        <label className="flex items-center gap-2 text-xs" style={{ color: "var(--char)" }}>
                          <input type="checkbox" checked={!!d.disabled}
                                    onChange={(e) => patch(row.code, "disabled", e.target.checked)}
                                    data-testid={`admin-promo-disabled-${row.code}`} />
                          <span>Disabled</span>
                        </label>
                        <label className="flex items-center gap-2 text-xs" style={{ color: "var(--char)" }}>
                          <input type="checkbox" checked={!!d.unlimited_per_user}
                                    onChange={(e) => patch(row.code, "unlimited_per_user", e.target.checked)}
                                    data-testid={`admin-promo-unlimited-${row.code}`} />
                          <span>Unlimited per user</span>
                        </label>
                      </div>
                    </div>

                    {isDirty(row.code) && (
                      <div className="text-xs mt-2" style={{ color: "#873F00" }} data-testid={`admin-promo-dirty-${row.code}`}>
                        Unsaved changes — click Save to apply.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Small numeric coercion so the UI treats undefined/null used_count / max_uses
// as zero — matches how the backend seeds fresh rows.
const int = (v) => (typeof v === "number" ? v : parseInt(v || 0, 10) || 0);

const Field = ({ label, value, onChange, type = "text", mono = false, testid }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider block mb-1" style={{ color: "var(--muted)" }}>{label}</label>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm rounded"
      style={{ border: "1px solid var(--hairline)", fontFamily: mono ? "'JetBrains Mono', ui-monospace, monospace" : undefined }}
      data-testid={testid}
    />
  </div>
);

export default AdminPromos;
